import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    ScoreboardWrapper,
    HeaderWrapper,
    VerticalGameMobileHeader,
    VerticalMiniWrapper
} from './HeaderScoreboard.style';
import Overlay from '@gameday/components/Overlay/Overlay';
import GDBREAKPOINTS, { MODAL_BREAKPOINT_NUM } from '@gameday/components/themes/breakpoints';
import { useMedia } from '@mlb/react-patterns/v4/utils/hooks/useMedia/useMedia';
import { ScoreboardModalContent, ModalWrapper } from '@gameday/components/Overlay/overlay.style';
import { ModalTypes } from '@gameday/components/AtBat/atbat.style';
import useTracking from '@gameday/utils/hooks/useTracking';
import MlbNativeWrapper from '@gameday/utils/MlbNativeWrapper';
import { useNavClickProxy } from '@gameday/utils/hooks/useNavClickProxy';

import useGamePickerFocusTrap from './useGamePickerFocusTrap';
import ConnectionErrorContainer from '@gameday/components/ConnectionError/containers/ConnectionErrorContainer';
import GamePkSync from './GamePkSync';

const { PORTAL_LESS, SCOREBOARD_VERTICAL } = ModalTypes;
const MODAL_BREAKPOINT = `${MODAL_BREAKPOINT_NUM + 1}px`;
const animation = { delay: 0.2, stiffness: 100 };
const verticalContentProps = {
    variants: {
        hide: {
            y: '100%',
            transition: { stiffness: 100 }
        },
        show: {
            y: 0,
            transition: animation
        }
    }
};

const trackingCfg = {
    componentName: ['Vertical Scoreboard'],
    activity: 'Game Picker Click',
    text: 'Gameday'
};

const HeaderScoreboard = ({
    componentMap
}) => {
    const Tracking = useTracking();
    const showGamedaySb = useMedia({ minWidth: GDBREAKPOINTS.XXL_MIN }); // the breakpoint to switch between OOT and vertical
    const usePortalLess = useMedia({ minWidth: `${MODAL_BREAKPOINT}` }); // the breakpoint to switch between desktop and mobile vertical sb
    const overlayPortalRef = useRef(null);
    const mlbNativeJsBridge = useRef(null);
    const scoreboardLinkRef = useRef(null);
    const contentRef = useRef(null);
    const [portalProps, setPortalProps] = useState(null);

    const [isOpen, setOpen] = useState(false);
    const [portalLessContentProps, setPortalLessContentProps] = useState();
    const onClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);
    const onOpen = useCallback(() => {
        setOpen(true);
    }, [setOpen]);
    const onArrowClick = useCallback(() => {
        setOpen(!isOpen);
        Tracking.track.track_click(trackingCfg);
    }, [setOpen, isOpen]);

    let overlayContainer = null;
    const {
        header,
        scoreboard
    } = componentMap;

    const nativeArrowClick = useCallback((cfg) => {
        const { button, value } = cfg;
        if (button === 'gamePicker') {
            if (value === 'open') {
                onOpen();
            }
            if (value === 'closed') {
                onClose();
            }
        }
    }, [onOpen, onClose]);
    useNavClickProxy(nativeArrowClick);

    useEffect(() => {
        if (typeof document !== 'undefined') {
            overlayContainer = overlayPortalRef?.current;
            setPortalProps(usePortalLess
                ? {
                    disablePortal: true,
                    container: overlayContainer
                }
                : null);
        }
    }, [usePortalLess]);

    useEffect(() => {
        const headerHeight = overlayPortalRef?.current?.clientHeight;
        const height = headerHeight ? window.innerHeight - headerHeight : window.innerHeight;
        setPortalLessContentProps({
            variants: {
                hide: {
                    y: `${height * -1}px`,
                    transition: animation
                },
                show: {
                    y: 0,
                    transition: animation
                }
            }
        });
    }, []);

    MlbNativeWrapper.then((results) => {
        const { updateNav } = results;
        mlbNativeJsBridge.current = {
            updateNav
        };
    });

    useEffect(() => {
        const { updateNav } = mlbNativeJsBridge?.current || {};
        if (updateNav && typeof updateNav === 'function') {
            updateNav({ button: 'gamePicker', value: isOpen ? 'open' : 'closed' });
        }
    }, [isOpen, mlbNativeJsBridge?.current]);

    useGamePickerFocusTrap({
        contentRef,
        openerRef: scoreboardLinkRef,
        isFocusableContentActive: isOpen,
        keepFocusTrapInModal: !usePortalLess
    });

    // The Modal component gives focus to the content container. This will force
    // the focus to stay on the Gameday link instead when first opened.
    useEffect(() => {
        if (isOpen && scoreboardLinkRef.current) {
            setTimeout(() => scoreboardLinkRef.current.focus(), 0);
        }
    }, [isOpen, scoreboardLinkRef?.current]);

    return (
        <>
            <HeaderWrapper ref={overlayPortalRef}>
                {header && (
                    <componentMap.header
                        showGamedaySb={showGamedaySb}
                        sbVisibility={isOpen}
                        onClick={onArrowClick}
                        hasScoreboard={!!scoreboard}
                        linkRef={scoreboardLinkRef}
                    />
                )}
                <ConnectionErrorContainer />
                <GamePkSync />
            </HeaderWrapper>
            { showGamedaySb && scoreboard && <ScoreboardWrapper $sbVisibility={showGamedaySb}><componentMap.scoreboard /></ScoreboardWrapper> }
            { !showGamedaySb && scoreboard && (
                <ModalWrapper $usePortalLess={usePortalLess}>
                    <Overlay
                        isOpen={isOpen}
                        onClose={onClose}
                        onClick={onClose}
                        portalProps={portalProps}
                        type={usePortalLess ? PORTAL_LESS : SCOREBOARD_VERTICAL}
                        ContentComponent={ScoreboardModalContent}
                        disableCloseButton={ usePortalLess }
                        ref={contentRef}
                        contentProps={usePortalLess ? portalLessContentProps : verticalContentProps}
                    >
                        <VerticalGameMobileHeader>Games</VerticalGameMobileHeader>
                        <VerticalMiniWrapper>
                            <componentMap.scoreboard gameCardMode={'vertical'} gameChangeHandler={onClose}/>
                        </VerticalMiniWrapper>
                    </Overlay>
                </ModalWrapper>
            )}
        </>
    );
};

HeaderScoreboard.propTypes = {
    componentMap: PropTypes.object
};

export default HeaderScoreboard;
